/**
 * เสี่ยงโชคและการ์ดมงคล (เทวาลัยออนไลน์ srikanett.com) ในศูนย์สมาชิก
 * ข้อมูลจาก Cloud Function templeApi (action my) — ตรวจ LINE ID token เดียวกับหน้าสมาชิก
 * เลข/การ์ดจะมีเฉพาะรายการที่จ่ายแล้ว (เซิร์ฟเวอร์ไม่ส่งของรายการค้างจ่าย)
 */

export const TEMPLE_API_URL = 'https://asia-southeast1-srikanett-order.cloudfunctions.net/templeApi'
export const TEMPLE_SITE_FALLBACK = 'https://srikanett-temple.web.app'

export type TempleKind = 'incense' | 'scratch'

export interface TemplePlay {
  playId: string
  kind: TempleKind
  status: string
  amount: number
  paid: boolean
  free: boolean
  pending: boolean
  slipPending?: boolean
  slipRejected?: boolean
  createdAt: string
  paidAt?: string
  result?: { digits3?: string[]; digits2?: string[] }
  card?: { title: string; text: string; kind: 'success' | 'blessing' }
}

export interface TempleSiteConfig {
  liff: Record<'1' | '2', string>
  siteUrl: string
}

export interface TempleLatest {
  playId: string
  digits3: string[]
  digits2: string[]
  paidAt: string
}

export type TempleFilter = 'all' | TempleKind

export const KIND_LABEL: Record<TempleKind, string> = { incense: 'จุดธูป', scratch: 'การ์ดมงคล' }

/** ลิงก์เข้าเทวาลัย (ผ่าน LIFF ของบอทเดียวกันถ้ามี — ล็อกอินต่อเนื่อง) · มี play = เปิดรายการนั้นต่อ */
export function templeUrl(config: TempleSiteConfig | null, botId: string, play?: Pick<TemplePlay, 'playId' | 'kind'>): string {
  const bot = botId === '1' ? '1' : '2'
  const params = new URLSearchParams()
  if (play) {
    params.set('ritual', play.kind)
    params.set('play', play.playId)
  }
  params.set('bot', bot)
  const liffId = config?.liff?.[bot]
  if (liffId) return `https://liff.line.me/${liffId}?${params.toString()}`
  const site = (config?.siteUrl || TEMPLE_SITE_FALLBACK).replace(/\/+$/, '')
  return `${site}/?${params.toString()}`
}

export function filterPlays(plays: TemplePlay[], filter: TempleFilter): TemplePlay[] {
  return filter === 'all' ? plays : plays.filter((p) => p.kind === filter)
}

/** ข้อความสถานะสั้นของรายการที่ยังไม่จบ — ว่าง = จบแล้ว (แสดงผลแทน) */
export function playStatusText(play: TemplePlay): string {
  if (play.paid || play.free) return ''
  if (play.slipRejected) return 'สลิปไม่ผ่าน · แตะเพื่อแนบใหม่'
  if (play.slipPending || play.status === 'รอยืนยัน') return 'รอแอดมินตรวจสลิป'
  return 'รอชำระเงิน · แตะเพื่อชำระต่อ'
}

export function formatPlayTime(iso: string | undefined): string {
  const t = Date.parse(iso || '')
  if (!Number.isFinite(t)) return ''
  const d = new Date(t + 7 * 3600 * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`
}

async function callTemple<T>(body: Record<string, unknown>): Promise<T> {
  const res = await fetch(TEMPLE_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string } & T
  if (!res.ok || data.ok === false) throw new Error(data.message || 'โหลดรายการไม่สำเร็จ กรุณาลองใหม่')
  return data
}

export async function fetchTemplePlays(idToken: string): Promise<TemplePlay[]> {
  const data = await callTemple<{ plays: TemplePlay[] }>({ action: 'my', idToken })
  return Array.isArray(data.plays) ? data.plays : []
}

export async function fetchTempleConfig(): Promise<TempleSiteConfig | null> {
  try {
    const data = await callTemple<{ config: TempleSiteConfig }>({ action: 'config' })
    return data.config || null
  } catch {
    return null
  }
}
